<!--
	SPDX-FileCopyrightText: Ferdinand Thiessen <opensource@fthiessen.de>
	SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<ul :class="$style.list">
		<CheckEntry
			v-for="(entry, index) of setupChecks"
			ref="checkEntryElement"
			:key="index"
			:name="entry.name ?? '?'"
			:description="formatDescription(entry)"
			:severity="entry.severity"
			@open-entry="onOpenEntry(entry)" />
	</ul>
</template>

<script setup lang="ts">
import type { ISetupCheck } from '../types'
import CheckEntry from './CheckEntry.vue'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = defineProps<{
	setupChecks: ISetupCheck[]
}>()

const emit = defineEmits<{
	(e: 'show-details', v: ISetupCheck): void
}>()

/**
 * Format description of a setup check to handle RichObjects
 * Basically replaces all RichObjects with the plain name or ID and only shows the first line
 * @param setupcheck The SetupCheck to format
 */
const formatDescription = (setupcheck: ISetupCheck) => {
	// Only show the first line without trailing colon
	const description = (setupcheck.description ?? '')
		.split('\n', 2)[0]
		.trim()
		.replace(/:$/, '')
	const placeholders = description.split(/(\{[a-z\-_.0-9]+\})/gi).map((entry) => {
		const matches = entry.match(/^\{([a-z\-_.0-9]+)\}$/i)
		// just return plain string nodes as text
		if (!matches) {
			return entry
		}
		// return component instance if argument is an object
		const argumentId = matches[1]
		const argument = setupcheck.descriptionParameters[argumentId]
		return argument?.name ?? argument.id
	})
	return placeholders.join('')
}

/**
 * Handle showing the modal by emitting an event
 * @param v The setup check to show
 */
const onOpenEntry = (v: ISetupCheck) => emit('show-details', v)
</script>

<style module lang="scss">
// The list content
.list {
	display: flex;
	flex-direction: column;
	justify-content: start;
	// for the focus outline
	margin: 4px;
}
</style>
